export default function indicationContentDropdown(config = {}) {
    return {
        // Initial state
        editMode: config.editMode || false,
        activeIndex: config.activeIndex !== undefined ? config.activeIndex : null,
        dropdownOpen: false,
        selectedLabel: "",
        focusIndex: 0,
        lastFocusedElement: null,

        // Initialize component
        init() {
            this.updateSelectedLabel();
        },

        // Get all option buttons
        getOptionButtons() {
            return this.$refs.dropdownList?.querySelectorAll("[role='option']") || [];
        },

        // Focus option by index
        focusOption(index) {
            const buttons = this.getOptionButtons();
            if (buttons[index]) {
                buttons[index].focus();
                this.focusIndex = index;
            }
        },

        // Toggle dropdown open/closed
        toggleDropdown() {
            if (this.dropdownOpen) {
                this.closeDropdown();
            } else {
                this.openDropdown();
            }
        },

        // Open dropdown with focus management
        openDropdown() {
            this.lastFocusedElement = document.activeElement;
            this.dropdownOpen = true;

            // Focus the selected option or first option
            this.$nextTick(() => {
                this.focusIndex = this.activeIndex !== null ? this.activeIndex : 0;
                this.focusOption(this.focusIndex);
            });
        },

        // Close dropdown with focus restoration
        closeDropdown() {
            this.dropdownOpen = false;
        },

        // Select an item
        selectItem(index, label) {
            this.activeIndex = index;
            this.selectedLabel = label;
            this.closeDropdown();
        },

        // Update selected label based on active index
        updateSelectedLabel() {
            if (this.activeIndex === null) {
                this.selectedLabel = "";
                return;
            }
            const buttons = this.getOptionButtons();
            const activeButton = buttons[this.activeIndex];
            if (activeButton) {
                this.selectedLabel = activeButton.querySelector("strong")?.textContent?.trim() || "";
            }
        },

        // Handle trigger button keyboard events
        handleTriggerKeyDown(e) {
            switch (e.key) {
                case "ArrowDown":
                case "ArrowUp":
                case "Enter":
                case " ":
                    e.preventDefault();
                    if (!this.dropdownOpen) {
                        this.openDropdown();
                    }
                    break;
                case "Escape":
                    if (this.dropdownOpen) {
                        e.preventDefault();
                        this.closeDropdown();
                    }
                    break;
            }
        },

        // Handle option keyboard events
        handleOptionKeyDown(e) {
            const buttons = this.getOptionButtons();
            const maxIndex = buttons.length - 1;

            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    this.focusIndex = this.focusIndex < maxIndex ? this.focusIndex + 1 : 0;
                    this.focusOption(this.focusIndex);
                    break;

                case "ArrowUp":
                    e.preventDefault();
                    this.focusIndex = this.focusIndex > 0 ? this.focusIndex - 1 : maxIndex;
                    this.focusOption(this.focusIndex);
                    break;

                case "Home":
                    e.preventDefault();
                    this.focusIndex = 0;
                    this.focusOption(this.focusIndex);
                    break;

                case "End":
                    e.preventDefault();
                    this.focusIndex = maxIndex;
                    this.focusOption(this.focusIndex);
                    break;

                case "Enter":
                case " ":
                    {
                        e.preventDefault();
                        const selectedButton = buttons[this.focusIndex];
                        if (selectedButton) {
                            const index = parseInt(selectedButton.dataset.index);
                            const label = selectedButton.querySelector("strong")?.textContent?.trim() || "";
                            this.selectItem(index, label);
                        }
                    }
                    break;

                case "Escape":
                    e.preventDefault();
                    this.closeDropdown();
                    break;

                case "Tab":
                    // Allow natural tab behavior but close dropdown
                    this.closeDropdown();
                    break;
            }
        },

        // Generic keyboard handler for container
        handleKeyDown(e) {
            // Handle any container-level keyboard events if needed
            if (e.key === "Escape" && this.dropdownOpen) {
                e.preventDefault();
                this.closeDropdown();
            }
        },

        // Watch for activeIndex changes
        $watch: {
            activeIndex() {
                this.updateSelectedLabel();
            },

            dropdownOpen(isOpen) {
                // Handle focus management when dropdown state changes
                if (!isOpen && document.activeElement?.closest(".indication-content-dropdown-content")) {
                    this.$refs.triggerButton?.focus();
                }
            }
        }
    };
}
